import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * CrmTaskService - Angular service for Task Management in the
 * Sales Team Collaboration module.
 *
 * Backend base: /api/crmTask  (ecom-svc-admin)
 */
@Injectable({
  providedIn: 'root'
})
export class CrmTaskService {

  private readonly base = environment.serverAPI4 + '/api/crmTask';

  constructor(private http: HttpClient) {}

  // ---------------------------------------------------------------------------
  // CRUD
  // ---------------------------------------------------------------------------

  /** Create a new task. */
  createTask(task: any): Observable<any> {
    return this.http.post(this.base, task);
  }

  /** Get all tasks for the tenant (paginated). */
  getAllTasks(page = 0, size = 20, sort = 'createdDate,desc'): Observable<any> {
    return this.http.get(`${this.base}?page=${page}&size=${size}&sort=${sort}`);
  }

  /** Get a task by ID. */
  getTaskById(id: number): Observable<any> {
    return this.http.get(`${this.base}/${id}`);
  }

  /** Get tasks linked to a specific CRM resource. */
  getTasksByResource(resourceType: string, resourceId: number,
                     page = 0, size = 20): Observable<any> {
    return this.http.get(
      `${this.base}/resource/${resourceType}/${resourceId}?page=${page}&size=${size}`
    );
  }

  /** Get tasks assigned to a specific user. */
  getTasksByAssignee(userId: number, page = 0, size = 20): Observable<any> {
    return this.http.get(
      `${this.base}/assignee/${userId}?page=${page}&size=${size}`
    );
  }

  /** Full update of a task. */
  updateTask(task: any): Observable<any> {
    return this.http.put(this.base, task);
  }

  // ---------------------------------------------------------------------------
  // STATE TRANSITIONS
  // ---------------------------------------------------------------------------

  /** Reassign a task to a different user. */
  reassignTask(taskId: number, body: {
    newAssigneeId: number;
    newAssigneeName: string;
    reassignedByName: string;
  }): Observable<any> {
    return this.http.put(`${this.base}/${taskId}/reassign`, body);
  }

  /** Mark a task as completed. */
  completeTask(taskId: number, completedBy: string): Observable<any> {
    return this.http.put(
      `${this.base}/${taskId}/complete?completedBy=${encodeURIComponent(completedBy)}`,
      {}
    );
  }

  /** Soft-delete a task. */
  deleteTask(taskId: number): Observable<any> {
    return this.http.delete(`${this.base}/${taskId}`);
  }

  // ---------------------------------------------------------------------------
  // ADMIN TRIGGERS
  // ---------------------------------------------------------------------------

  /** Trigger overdue detection for the current tenant. */
  markOverdueTasks(): Observable<any> {
    return this.http.post(`${this.base}/overdue/mark`, {});
  }
}
